<!--
  Fornitori di mezzi (COD8) — con l'impaginazione delle liste Advisor e
  Clienti: card che filtrano lo stato, intestazione grigia, riga che apre la
  modifica.

  Chi ci noleggia pulmini e navette per i Transfer. È una risorsa SIMBA —
  costi interni, nessun tenant — quindi il backend la riserva ai ruoli
  globali: con un utente partner queste chiamate rispondono 403, e la pagina
  lo dice invece di mostrare una lista vuota.

  Due cose non ovvie:

  1) NON SI CANCELLA, SI DISATTIVA. Un fornitore con noleggi alle spalle
     sparirebbe dai resoconti COD8 dei mesi già chiusi. `is_active` lo toglie
     dalle tendine dei nuovi noleggi e lo lascia nello storico.

  2) IL COSTO NON STA QUI. L'anagrafica non ha tariffe: il costo è del singolo
     noleggio (`vehicle_rental.cost`), perché lo stesso fornitore può darci
     una navetta a prezzi diversi in giornate diverse. Il totale per fornitore
     si legge dal COD8.
-->
<template>
  <div class="sup">
    <!-- Intestazione -->
    <div class="sup__head">
      <div>
        <h1 class="sup__title">
          Fornitori
        </h1>
        <p class="sup__sub">
          Chi ci noleggia i mezzi per i Transfer — i costi finiscono nel COD8.
        </p>
      </div>
      <div class="sup__headActions">
        <el-input
          v-model="search"
          class="sup__search"
          placeholder="Cerca ragione sociale, P.IVA, mail…"
          clearable
          @input="onSearchInput"
        />
        <el-button
          type="primary"
          @click="openNew"
        >
          Nuovo fornitore
        </el-button>
      </div>
    </div>

    <div
      v-if="forbidden"
      class="sup__warn"
    >
      <b>Sezione riservata a Simba.</b>
      I fornitori sono un costo interno: il backend ne consente la gestione
      solo ai ruoli globali (superadmin). Con l'utenza attuale la lista non è
      accessibile.
    </div>

    <!-- KPI: sono anche il filtro dello stato -->
    <div class="sup__kpis">
      <button
        v-for="k in kpis"
        :key="k.key"
        class="sup__kpi"
        :class="{ 'is-active': segment === k.key, 'is-muted': k.key === 'disattivati' }"
        type="button"
        @click="segment = k.key"
      >
        <span class="sup__kpiNum">{{ k.value === null ? '—' : k.value }}</span>
        <span class="sup__kpiLbl">{{ k.label }}</span>
      </button>
    </div>

    <!-- Tabella -->
    <div class="sup__card">
      <el-table
        v-loading="loading"
        :data="visibleRows"
        style="width: 100%"
        :empty-text="emptyText"
        @row-click="openEdit"
      >
        <el-table-column
          label="Fornitore"
          min-width="240"
        >
          <template #default="scope">
            <div class="sup__who">
              <span class="sup__avatar">{{ initials(scope.row.company_name) }}</span>
              <div>
                <div class="sup__name">
                  {{ scope.row.company_name }}
                </div>
                <div class="sup__meta">
                  {{ scope.row.piva ? `P.IVA ${scope.row.piva}` : 'P.IVA non indicata' }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Contatti"
          min-width="210"
        >
          <template #default="scope">
            <div>{{ scope.row.mail || '—' }}</div>
            <div class="sup__meta sup__num">
              {{ scope.row.phone_number || '' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Note"
          min-width="220"
        >
          <template #default="scope">
            <span
              v-if="scope.row.note"
              class="sup__note"
            >{{ scope.row.note }}</span>
            <span
              v-else
              class="sup__dash"
            >—</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="140"
        >
          <template #default="scope">
            <StatusPill
              :kind="scope.row.is_active ? 'ok' : 'muted'"
              :label="scope.row.is_active ? 'Attivo' : 'Disattivato'"
            />
          </template>
        </el-table-column>

        <el-table-column
          label="Azioni"
          width="200"
          align="right"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              size="small"
              plain
              :loading="busyId === scope.row.id"
              @click.stop="toggleActive(scope.row)"
            >
              {{ scope.row.is_active ? 'Disattiva' : 'Riattiva' }}
            </el-button>
            <el-button
              type="primary"
              size="small"
              @click.stop="openEdit(scope.row)"
            >
              Modifica
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="sup__foot">
        <span class="sup__footInfo">
          {{ visibleRows.length }} {{ visibleRows.length === 1 ? 'fornitore' : 'fornitori' }}
        </span>
      </div>
    </div>

    <!-- ------------------------- creazione / modifica ---------------------- -->
    <el-dialog
      v-model="dialog"
      :title="draft.id ? 'Modifica fornitore' : 'Nuovo fornitore'"
      width="560px"
    >
      <div class="sup__form">
        <div class="sup__fld">
          <label for="sup-name">Ragione sociale</label>
          <el-input
            id="sup-name"
            v-model="draft.company_name"
            placeholder="Es. Autonoleggi Rossi SRL"
          />
        </div>
        <div class="sup__grid2">
          <div class="sup__fld">
            <label for="sup-piva">Partita IVA</label>
            <el-input
              id="sup-piva"
              v-model="draft.piva"
            />
          </div>
          <div class="sup__fld">
            <label for="sup-phone">Telefono</label>
            <el-input
              id="sup-phone"
              v-model="draft.phone_number"
            />
          </div>
        </div>
        <div class="sup__fld">
          <label for="sup-mail">Email</label>
          <el-input
            id="sup-mail"
            v-model="draft.mail"
          />
        </div>
        <div class="sup__fld">
          <label for="sup-note">Note <span class="sup__opt">(facoltative)</span></label>
          <el-input
            id="sup-note"
            v-model="draft.note"
            type="textarea"
            :rows="3"
          />
        </div>
      </div>
      <template #footer>
        <el-button @click="dialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="saving"
          :disabled="!draft.company_name.trim()"
          @click="save"
        >
          Salva
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import supplierAPI from '../../services/supplierAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';

const emptyDraft = () => ({
  id: '', company_name: '', piva: '', mail: '', phone_number: '', note: '',
});

export default {
  name: 'SupplierPage',
  components: { StatusPill },

  setup() {
    const rows = ref([]);
    const loading = ref(false);
    const forbidden = ref(false);
    const search = ref('');
    const segment = ref('tutti'); // tutti | attivi | disattivati
    const dialog = ref(false);
    const saving = ref(false);
    const busyId = ref('');
    const draft = reactive(emptyDraft());

    const kpis = computed(() => {
      const known = !forbidden.value;
      const active = rows.value.filter((r) => r.is_active).length;
      return [
        { key: 'tutti', label: 'Fornitori', value: known ? rows.value.length : null },
        { key: 'attivi', label: 'Attivi', value: known ? active : null },
        { key: 'disattivati', label: 'Disattivati', value: known ? rows.value.length - active : null },
      ];
    });

    const visibleRows = computed(() => {
      if (segment.value === 'attivi') return rows.value.filter((r) => r.is_active);
      if (segment.value === 'disattivati') return rows.value.filter((r) => !r.is_active);
      return rows.value;
    });

    const emptyText = computed(() => {
      if (forbidden.value) return 'Sezione riservata a Simba.';
      if (search.value.trim()) return 'Nessun fornitore corrisponde alla ricerca.';
      if (segment.value === 'attivi') return 'Nessun fornitore attivo.';
      if (segment.value === 'disattivati') return 'Nessun fornitore disattivato.';
      return 'Nessun fornitore registrato.';
    });

    const load = async () => {
      loading.value = true;
      try {
        rows.value = await supplierAPI.getSuppliers({ search: search.value });
        forbidden.value = false;
      } catch (e) {
        // 403 non è un guasto: è la risorsa riservata a Simba. Va detto,
        // non trasformato in "nessun fornitore".
        forbidden.value = e?.response?.status === 403;
        if (!forbidden.value) {
          console.error('Fornitori non caricati:', e);
          ElMessage.error('Impossibile caricare i fornitori');
        }
        rows.value = [];
      } finally {
        loading.value = false;
      }
    };

    let timer = null;
    const onSearchInput = () => {
      clearTimeout(timer);
      timer = setTimeout(load, 350);
    };

    const openNew = () => {
      Object.assign(draft, emptyDraft());
      dialog.value = true;
    };

    const openEdit = (row) => {
      Object.assign(draft, {
        id: row.id,
        company_name: row.company_name || '',
        piva: row.piva || '',
        mail: row.mail || '',
        phone_number: row.phone_number || '',
        note: row.note || '',
      });
      dialog.value = true;
    };

    const save = async () => {
      saving.value = true;
      const body = {
        company_name: draft.company_name.trim(),
        piva: draft.piva.trim() || null,
        mail: draft.mail.trim() || null,
        phone_number: draft.phone_number.trim() || null,
        note: draft.note.trim() || null,
      };
      try {
        if (draft.id) await supplierAPI.updateSupplier(draft.id, body);
        else await supplierAPI.createSupplier({ ...body, is_active: true });
        ElMessage.success(draft.id ? 'Fornitore aggiornato.' : 'Fornitore creato.');
        dialog.value = false;
        await load();
      } catch (e) {
        console.error('Salvataggio fornitore fallito:', e);
        ElMessage.error(
          e?.response?.status === 403
            ? 'Operazione riservata ai ruoli globali.'
            : 'Salvataggio non riuscito',
        );
      } finally {
        saving.value = false;
      }
    };

    const toggleActive = async (row) => {
      busyId.value = row.id;
      try {
        await supplierAPI.setSupplierActive(row.id, !row.is_active);
        await load();
      } catch (e) {
        console.error('Cambio stato fallito:', e);
        ElMessage.error('Cambio stato non riuscito');
      } finally {
        busyId.value = '';
      }
    };

    // Iniziali della ragione sociale, senza la forma societaria finale.
    const initials = (name) =>
      String(name || '')
        .replace(/\b(s\.?r\.?l\.?s?|s\.?p\.?a\.?|s\.?n\.?c\.?|s\.?a\.?s\.?)$/i, '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase() || '?';

    onMounted(load);

    return {
      rows, visibleRows, loading, forbidden, search, segment, kpis, emptyText,
      dialog, draft, saving, busyId,
      onSearchInput, openNew, openEdit, save, toggleActive, initials,
    };
  },
};
</script>

<style scoped>
.sup {
  padding: 4px 0 32px;
}

/* ---- intestazione ---- */
.sup__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.sup__title {
  margin: 0;
  font-size: 22px;
  color: var(--text-primary);
}
.sup__sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--text-tertiary);
}
.sup__headActions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.sup__search {
  width: 280px;
}

.sup__warn {
  margin-bottom: 14px;
  padding: 10px 13px;
  border-radius: 9px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--state-attn);
  background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
}

/* ---- KPI cliccabili (fanno anche da filtro dello stato) ---- */
.sup__kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
@media (max-width: 720px) {
  .sup__kpis { grid-template-columns: 1fr; }
}
.sup__kpi {
  text-align: left;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 13px 16px;
  cursor: pointer;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  font-family: inherit;
}
.sup__kpi:hover { border-color: var(--brand-violet); }
.sup__kpi:focus-visible { outline: 2px solid var(--brand-violet); outline-offset: 2px; }
.sup__kpi.is-active {
  border-color: var(--brand-violet);
  box-shadow: inset 0 0 0 1px var(--brand-violet);
}
.sup__kpiNum {
  display: block;
  font-size: 24px;
  font-weight: 750;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
}
.sup__kpi.is-muted .sup__kpiNum { color: var(--state-muted); }
.sup__kpiLbl {
  display: block;
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

/* ---- tabella ---- */
.sup__card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.sup__who {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sup__avatar {
  width: 32px;
  height: 32px;
  flex: none;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--avatar-bg);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.sup__name { font-weight: 600; color: var(--text-primary); }
.sup__meta { font-size: 12px; color: var(--text-tertiary); }
.sup__num { font-variant-numeric: tabular-nums; }
.sup__dash { color: var(--text-tertiary); }
.sup__note {
  font-size: 12.5px;
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ---- piede ---- */
.sup__foot {
  padding: 12px 14px;
  border-top: 1px solid var(--border-color);
}
.sup__footInfo {
  font-size: 12.5px;
  color: var(--text-tertiary);
}

/* la riga apre la modifica: lo si deve capire */
.sup :deep(.el-table__row) { cursor: pointer; }

/* Intestazione della tabella grigio chiaro come nelle liste Clienti e
   Advisor: element-overrides.css impone a tutte le tabelle il gradiente
   viola con !important, quindi serve !important anche qui. */
.sup :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 11px 12px;
  border-bottom: 1px solid var(--border-color);
}

/* ---- dialogo ---- */
.sup__form { display: flex; flex-direction: column; gap: 14px; }
.sup__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 560px) {
  .sup__grid2 { grid-template-columns: 1fr; }
}
.sup__fld { display: flex; flex-direction: column; min-width: 0; }
.sup__fld label {
  display: block;
  margin-bottom: 5px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.sup__opt { text-transform: none; letter-spacing: 0; font-weight: 500; }
</style>
